import gsap from 'gsap';
import throttle from '../../../assets/js/base/_throttle.js';

/**
 * Manages the site intro overlay session state
 * The overlay is shown only once per browser session
 *
 * Session Storage Key: 'aspen_intro_overlay_shown'
 *
 * To reset for testing, run in console:
 * sessionStorage.removeItem('aspen_intro_overlay_shown');
 */

function init( node ) {
	if ( ! ( node instanceof HTMLElement ) ) {
		return;
	}

	// Disable scroll-behavior: smooth set in reset.scss which causes issues with ScrollTrigger
	document.documentElement.style.scrollBehavior = 'auto';

	const {
		introOverlay = node.querySelector( '.site-intro-overlay' ),
		heroCallout = node.querySelector( '.hero__callout' ),
		rotator = node.querySelector( '.tagline-rotator' ),
		fixedText = node.querySelector( '.tagline-rotator__fixed-text' ),
		lines = node.querySelector( '.tagline-rotator__lines' ),
	} = node;

	const hero = node;

	const taglines = Array.from( node.querySelectorAll( '.tagline-rotator__line' ) );
	const finalTagline = node.querySelector( '.tagline-rotator__line-final' );

	// Get only regular taglines (excluding the final one)
	const regularTaglines = taglines.filter( ( tagline ) => tagline !== finalTagline );

	// Check if overlay has been shown in this session
	const OVERLAY_SESSION_KEY = 'aspen_intro_overlay_shown';
	let hasShownOverlay = false;

	try {
		hasShownOverlay = sessionStorage.getItem( OVERLAY_SESSION_KEY ) === 'true';
	} catch ( e ) {
		// sessionStorage not available, default to false (show overlay)
		hasShownOverlay = false;
	}

	// Handle overlay visibility based on session state
	if ( introOverlay ) {
		if ( hasShownOverlay ) {
			// Overlay already shown, keep it hidden
			introOverlay.classList.add( 'is-hidden' );
		} else {
			// First time in session, show the overlay
			introOverlay.classList.add( 'should-show' );
		}
	}

	// Timeline
	const tl = gsap.timeline( { paused: true } ); // Start paused until overlay is gone

	/**
	 * Updates the width of the rotator to match the final tagline
	 * and handles responsive adjustments
	 */
	const updateRotatorWidth = () => {
		// Save state of currently visible tagline if any
		const visibleTagline = taglines.find( ( tag ) => gsap.getProperty( tag, 'autoAlpha' ) > 0.5 );
		const visibleTaglineProps = visibleTagline && {
			opacity: gsap.getProperty( visibleTagline, 'autoAlpha' ),
			y: gsap.getProperty( visibleTagline, 'y' ),
		};

		// Set up for measurement
		gsap.set( taglines, {
			autoAlpha: 0,
			position: 'absolute',
			visibility: 'hidden',
		} );

		gsap.set( finalTagline, {
			autoAlpha: 1,
			position: 'static',
			visibility: 'visible',
		} );

		// Get width of final tagline
		const finalTaglineWidth = finalTagline.getBoundingClientRect().width;

		// Reset visibility
		gsap.set( taglines, {
			autoAlpha: 0,
			position: 'absolute',
			visibility: 'visible',
		} );

		// Mobile vs desktop handling
		const isStacked = window.matchMedia( '(max-width: 991px)' ).matches;

		// Set appropriate width based on screen size
		gsap.set( rotator, {
			maxWidth: isStacked ? 'none' : `${ Math.ceil( finalTaglineWidth * 1.025 ) }px`,
		} );

		// Center fixed text initially or move it to left based on timeline progress
		const progress = tl.progress();
		const shouldCenter = progress === 0 || ( progress > 0 && progress < 0.2 );

		if ( shouldCenter ) {
			const fixedTextWidth = fixedText.getBoundingClientRect().width;
			const rotatorWidth = rotator.getBoundingClientRect().width;
			gsap.set( fixedText, { x: ( rotatorWidth - fixedTextWidth ) / 2 } );
		}

		// Restore visible tagline if needed
		if ( visibleTagline && visibleTaglineProps ) {
			gsap.set( visibleTagline, {
				autoAlpha: visibleTaglineProps.opacity,
				y: visibleTaglineProps.y,
				visibility: 'visible',
			} );
		}
	};

	// Initial setup
	updateRotatorWidth();

	// Set initial states
	gsap.set( taglines, { autoAlpha: 0, y: 0 } );
	gsap.set( finalTagline, { autoAlpha: 0, y: 0 } );
	gsap.set( lines, {
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
	} );

	// Hide rotator initially until overlay animation completes
	gsap.set( rotator, { autoAlpha: 0 } );

	// Constants for animation
	const ANIMATION = {
		duration: {
			initial: 0.25,
			standard: 0.4,
			pause: 1.2,
			overlayFade: 1,
		},
		distance: 30,
		ease: {
			in: 'power3.in',
			out: 'power3.out',
			initial: 'power2.out',
		},
	};

	// Build the animation sequence
	tl.to( rotator, {
		autoAlpha: 1,
		y: 0,
		duration: ANIMATION.duration.initial,
		ease: ANIMATION.ease.initial,
	} )
		.to( fixedText, {
			delay: 0.75,
			x: 0,
			duration: 0.75,
			ease: 'power3.inOut',
		} )
		.set( lines, { visibility: 'visible' }, '-=0.25' );

	// Animate regular taglines
	regularTaglines.forEach( ( tagline, index ) => {
		const isLast = index === regularTaglines.length - 1;
		const position = index === 0 ? '>+=0.4' : '+=0.1';

		// Animate tagline in
		tl.fromTo(
			tagline,
			{ autoAlpha: 0, y: ANIMATION.distance },
			{
				autoAlpha: 1,
				y: 0,
				duration: ANIMATION.duration.standard,
				ease: ANIMATION.ease.out,
			},
			position
		);

		// Pause then animate out
		tl.to( {}, { duration: ANIMATION.duration.pause } );

		if ( isLast ) {
			// Animate last regular tagline and fixed text out together
			tl.to(
				[
					tagline,
					fixedText,
				],
				{
					autoAlpha: 0,
					y: -ANIMATION.distance,
					duration: ANIMATION.duration.standard,
					ease: ANIMATION.ease.in,
				}
			);
		} else {
			// Animate non-last taglines out
			tl.to( tagline, {
				autoAlpha: 0,
				y: -ANIMATION.distance,
				duration: ANIMATION.duration.standard,
				ease: ANIMATION.ease.in,
			} );
		}
	} );

	// Animate final tagline in
	tl.fromTo(
		finalTagline,
		{ autoAlpha: 0, y: ANIMATION.distance },
		{
			autoAlpha: 1,
			y: 0,
			duration: ANIMATION.duration.standard,
			ease: ANIMATION.ease.out,
		},
		'+=0.1'
	);

	// Handle intro overlay video
	if ( introOverlay && ! hasShownOverlay ) {
		const introVideo = introOverlay.querySelector( 'video' );

		// Start the tagline animation timeline after overlay is gone
		const startMainAnimation = () => {
			// Mark overlay as shown in session storage
			try {
				sessionStorage.setItem( OVERLAY_SESSION_KEY, 'true' );
			} catch ( e ) {
				// sessionStorage not available, continue anyway
			}
			tl.play();
		};

		if ( introVideo ) {
			// Set up ended event listener and error handling
			const handleVideoEnd = () => {
				gsap.to( introOverlay, {
					autoAlpha: 0,
					duration: ANIMATION.duration.overlayFade,
					ease: ANIMATION.ease.out,
					onComplete: startMainAnimation,
				} );
			};

			introVideo.addEventListener( 'ended', handleVideoEnd );
			introVideo.addEventListener( 'error', handleVideoEnd );

			// Try to play the video
			const playPromise = introVideo.play();

			// Handle autoplay blocking
			if ( playPromise !== undefined ) {
				playPromise.catch( () => {
					// Play failed (autoplay blocked) - close overlay
					handleVideoEnd();
				} );
			}

			// Fallback timeout
			setTimeout( handleVideoEnd, 5000 );
		} else {
			// If there's an overlay but no video, just fade it out after a delay
			gsap.to( introOverlay, {
				autoAlpha: 0,
				duration: ANIMATION.duration.overlayFade,
				ease: ANIMATION.ease.out,
				delay: 1,
				onComplete: startMainAnimation,
			} );
		}
	} else {
		// No intro overlay or overlay already shown, start the animation immediately
		tl.play();
	}

	// Handle resize events
	const handleResize = () => {
		const wasPaused = tl.progress() > 0 && tl.paused();
		if ( tl.progress() > 0 ) tl.pause();
		updateRotatorWidth();

		ScrollTrigger.refresh();

		if ( tl.progress() > 0 && ! wasPaused ) tl.play();
	};

	// Debounce resize handling to improve performance
	const throttledResize = throttle( handleResize, 100 );
	window.addEventListener( 'resize', throttledResize );

	// Observe size changes
	const resizeObserver = new ResizeObserver( throttledResize );
	resizeObserver.observe( fixedText );
	resizeObserver.observe( finalTagline );

	// Pin the hero callout section
	if ( heroCallout ) {
		ScrollTrigger.create( {
			trigger: heroCallout,
			start: 'top top',
			endTrigger: '.entry-content',
			end: 'bottom top',
			pin: true,
			pinSpacing: false,
			anticipatePin: true,
		} );
	}
}

// Initialize
const heroElement = document.querySelector( '.hero' );
if ( heroElement ) {
	init( heroElement );
}
